import { cn } from "@newxsystems/shared";
import { DaylightCardLink } from "../../../../modules/content/components/DaylightCardLink/DaylightCardLink";
import { DaylightCommonContent } from "../../../../modules/content/components/DaylightCommonContent/DaylightCommonContent";
import { DaylightMedia } from "../../../../modules/content/components/DaylightMedia/DaylightMedia";
import { DaylightRichText } from "../../../../modules/content/components/DaylightRichText/DaylightRichText";
import { DaylightSection } from "../../../../modules/content/components/DaylightSection/DaylightSection";
import type { ITestimonialGridBlockProps } from "./TestimonialGridBlock.props";

const columns = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
} as const;
const spans = {
  1: "lg:col-span-1",
  2: "lg:col-span-2",
  3: "lg:col-span-3",
  4: "lg:col-span-4",
} as const;

export const TestimonialGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: ITestimonialGridBlockProps) => {
  if (!items?.length) return null;

  return (
    <DaylightSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-12 md:gap-16">
        <DaylightCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
          layout="rail"
        />
        <div
          className={cn(
            "grid grid-cols-1 border-l border-t border-border sm:grid-cols-2",
            columns[columnCount || 3],
          )}
        >
          {items.map((item, index) => (
            <DaylightCardLink
              className={cn(
                "flex flex-col border-b border-r border-border p-8 transition-colors duration-300 hover:bg-panel",
                spans[item.columnSpan || 1],
              )}
              key={index}
              link={item.cardLink}
            >
              <span className="daylight-label block text-northsea">
                {String(index + 1).padStart(2, "0")}
              </span>
              <blockquote className="mt-8 flex-1">
                <DaylightRichText
                  className="font-daylight-display text-2xl font-semibold leading-[1.15] tracking-[-0.03em] text-graphite"
                  content={item.quote}
                />
              </blockquote>
              <div className="mt-8 flex items-center gap-4 border-t border-border pt-6">
                {item.portrait ? (
                  <DaylightMedia
                    aspectRatio="1:1"
                    className="size-12 shrink-0"
                    media={item.portrait.image}
                    sizes="48px"
                  />
                ) : null}
                <div className="min-w-0">
                  <p className="daylight-label text-graphite">{item.name}</p>
                  {item.attribution ? (
                    <p className="daylight-label-micro mt-2 text-northsea">{item.attribution}</p>
                  ) : null}
                </div>
              </div>
            </DaylightCardLink>
          ))}
        </div>
      </div>
    </DaylightSection>
  );
};

TestimonialGridBlock.displayName = "Block::TestimonialGridBlock";
